import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { IconX } from "@tabler/icons-react";

import {
  check_authenticated,
  load_user,
  logout,
  refresh,
} from "../../redux/actions/auth/auth";
import DashboardNavbar from "../../components/navigation/DashboardNavbar";
import Toast from "../../components/Alert";

function DashboardLayout({
  children,
  refresh,
  check_authenticated,
  load_user,
  user_loading,
  user,
  logout,
  isAuthenticated,
}) {
  // === UI ===
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // === Efectos de sesión ===
  useEffect(() => {
    // Cargar estado de auth y usuario al montar
    refresh();
    check_authenticated();
    load_user();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirigir si no está autenticado (evita parpadeos esperando al loader)
  useEffect(() => {
    if (isAuthenticated === false && !user_loading) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, user_loading, navigate]);

  // === Handlers ===
  const handleLogout = () => {
    logout();
    setOpen(false);
    Toast({ tipo: "success", mensaje: "¡Te has desautenticado correctamente!" });
    navigate("/", { replace: true });
    // Opcional si necesitas limpiar completamente el estado de la SPA:
    // window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header fijo */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
        {/* Pasa el disparador para abrir el modal */}
        <DashboardNavbar onRequestLogout={() => setOpen(true)} user={user} />
      </header>

      {/* Contenido principal */}
      <main className="flex-1">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>

      {/* Modal de confirmación de logout */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={setOpen}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          {/* Panel */}
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end sm:items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-sm transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                  {/* Icono */}
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                    <IconX className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>

                  {/* Título y descripción */}
                  <div className="mt-4 text-center">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-gray-900"
                    >
                      Cerrar sesión
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-gray-600">
                      ¿Seguro que deseas cerrar sesión? Tendrás que volver a iniciar
                      para acceder a tu cuenta.
                    </p>
                  </div>

                  {/* Acciones */}
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="inline-flex justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm
                                bg-gradient-to-r from-[#ff1049] to-[#ff7826]
                                hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#ff1049]/40 focus:ring-offset-2"
                    >
                      Sí, cerrar sesión
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="inline-flex justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                    >
                      Cancelar
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

const mapStateToProps = (state) => ({
  user_loading: state.auth.user_loading,
  user: state.auth.user,
  isAuthenticated: state.auth.isAuthenticated,
});

export default connect(mapStateToProps, {
  refresh,
  check_authenticated,
  load_user,
  logout,
})(DashboardLayout);
